/**
 * World database — items, skills, mobs, NPCs, quests, classes.
 *
 * Ported from `L2-MECANICAS/src/game/WorldData.ts`. Every combat number
 * (ITEMS, SKILLS, MOBS stats, CLASS_PROFILES) is carried across unchanged;
 * what is added is the world this runs in:
 *
 *  - `vfx` on the skills, naming which of the five spells the cast
 *    fires (see `skillVfx.js`).
 *  - `visual` on the mobs, describing the procedural body the actor builds.
 *  - Positions in metres on a 2048 m snowfield rather than a 240-unit plane —
 *    the original layout scaled up so a walk between camps is a walk.
 *
 * The setting: Sastrugi Hold, a Rimewind Vanguard muster point sheltered in
 * the lee of a wind-carved ridge — the hub clearing baked into the heightfield
 * in `heightBake.fragment.wgsl`. Danger rises in rings the further a camp
 * sits from it: keltir nosing around the clearing edge, then rime wolves on
 * the lee slope, drift reavers holding the ridge break, the hollow wardens of
 * a garrison the Vanguard lost to a storm generations back, the stone
 * sentinels guarding what is left of it, and — far south, past all of it —
 * Skarn, the Drift Colossus. TELEPORTS are waypoints inside that same single
 * field, not separate zones; there is no level-streaming here, only distance.
 */

export const ITEMS = {
    short_sword: {
        id: "short_sword",
        name: "Short Sword",
        type: "weapon",
        slot: "weapon",
        icon: "🗡️",
        description: "A simple iron short sword commonly used by novice fighters.",
        price: 150,
        grade: "No-Grade",
        statsBonus: { patk: 12, critRate: 8 },
    },
    long_sword: {
        id: "long_sword",
        name: "Bastard Sword",
        type: "weapon",
        slot: "weapon",
        icon: "⚔️",
        description: "A heavy steel sword with sharp balance.",
        price: 1200,
        grade: "D-Grade",
        statsBonus: { patk: 32, critRate: 12 },
    },
    dual_katana: {
        id: "dual_katana",
        name: "Dual Katanas",
        type: "weapon",
        slot: "weapon",
        icon: "⚔️",
        description: "Twin blades refined for fast deadly slashing.",
        price: 8500,
        grade: "C-Grade",
        statsBonus: { patk: 78, critRate: 20 },
    },
    demon_staff: {
        id: "demon_staff",
        name: "Demon Staff",
        type: "weapon",
        slot: "weapon",
        icon: "🪄",
        description: "A sinister staff pulsating with dark magical energy.",
        price: 4500,
        grade: "D-Grade",
        statsBonus: { matk: 58, mp: 80 },
    },
    bow_of_peril: {
        id: "bow_of_peril",
        name: "Bow of Peril",
        type: "weapon",
        slot: "weapon",
        icon: "🏹",
        description: "An ornate recurve bow capable of piercing thick dragon hide.",
        price: 18000,
        grade: "B-Grade",
        statsBonus: { patk: 142, critRate: 25 },
    },
    wooden_armor: {
        id: "wooden_armor",
        name: "Wooden Armor Chest",
        type: "armor",
        slot: "chest",
        icon: "🛡️",
        description: "Light tunic reinforced with treated wooden slats.",
        price: 200,
        grade: "No-Grade",
        statsBonus: { pdef: 18, hp: 30 },
    },
    plate_armor: {
        id: "plate_armor",
        name: "Brigandine Armor",
        type: "armor",
        slot: "chest",
        icon: "🥋",
        description: "Heavy steel plate mail offering superior defense against physical strikes.",
        price: 3200,
        grade: "D-Grade",
        statsBonus: { pdef: 62, hp: 120 },
    },
    hp_potion: {
        id: "hp_potion",
        name: "Greater Healing Potion",
        type: "consumable",
        icon: "🧪",
        description: "Restores 150 HP immediately.",
        price: 35,
        grade: "No-Grade",
    },
    mp_potion: {
        id: "mp_potion",
        name: "Mana Potion",
        type: "consumable",
        icon: "🧪",
        description: "Restores 100 MP.",
        price: 50,
        grade: "No-Grade",
    },
    soulshot_ng: {
        id: "soulshot_ng",
        name: "Soulshot (No-Grade)",
        type: "consumable",
        icon: "✨",
        description: "Empowers weapon attacks with a burst of spirit energy, doubling physical damage.",
        price: 8,
        grade: "No-Grade",
    },
    spiritshot_ng: {
        id: "spiritshot_ng",
        name: "Spiritshot (No-Grade)",
        type: "consumable",
        icon: "🌌",
        description: "Empowers magic casting, doubling magic attack power.",
        price: 15,
        grade: "No-Grade",
    },
};

/**
 * Skills.
 *
 * Ranges are the original numbers read as metres — 3 m for melee, 15-18 m for
 * ranged — which lands close to retail's 40/900 units once you take a Lineage
 * unit as roughly 4 cm.
 */
export const SKILLS = {
    attack: {
        id: "attack",
        name: "Attack",
        icon: "⚔️",
        level: 1,
        mpCost: 0,
        cooldown: 0.8,
        castTime: 0,
        description: "Basic physical attack against targeted foe.",
        type: "active",
        requiredLevel: 1,
        spCost: 0,
        range: 3,
        vfx: null,
    },
    power_strike: {
        id: "power_strike",
        name: "Power Strike",
        icon: "💥",
        level: 1,
        mpCost: 12,
        cooldown: 3.5,
        castTime: 0.3,
        description: "A heavy strike dealing 2.5x physical damage.",
        type: "active",
        requiredLevel: 1,
        spCost: 50,
        range: 3,
        damageMultiplier: 2.5,
        physical: true,
        vfx: "sweep",
    },
    hydro_blast: {
        id: "hydro_blast",
        name: "Hydro Blast",
        icon: "🌊",
        level: 1,
        mpCost: 22,
        cooldown: 2.0,
        castTime: 1.2,
        description: "Unleashes a pressurized surge of water dealing high magic damage.",
        type: "active",
        requiredLevel: 1,
        spCost: 80,
        range: 18,
        damageMultiplier: 3.2,
        vfx: "ribbon",
    },
    ice_dagger: {
        id: "ice_dagger",
        name: "Ice Dagger",
        icon: "❄️",
        level: 1,
        mpCost: 15,
        cooldown: 4.0,
        castTime: 0.8,
        description: "Fires a razor-sharp shard of ice that slows enemy movement speed.",
        type: "active",
        requiredLevel: 3,
        spCost: 120,
        range: 15,
        damageMultiplier: 2.1,
        vfx: "crystallize",
        /** The slow the description always promised and never applied. */
        debuff: { id: "chill", duration: 6, mods: { moveSpeed: -1.6 }, name: "Chilled", icon: "❄️" },
    },
    shield_bash: {
        id: "shield_bash",
        name: "Shield Bash",
        icon: "🛡️",
        level: 1,
        mpCost: 18,
        cooldown: 8.0,
        castTime: 0.2,
        description: "Slam target with shield, interrupting spell cast and knocking them back.",
        type: "active",
        requiredLevel: 5,
        spCost: 200,
        range: 3,
        damageMultiplier: 1.5,
        physical: true,
        knockback: 4.5,
        vfx: "vortex",
    },
    ultimate_defense: {
        id: "ultimate_defense",
        name: "Ultimate Defense",
        icon: "🏰",
        level: 1,
        mpCost: 30,
        cooldown: 45.0,
        castTime: 0,
        description: "Immobilizes character while boosting P.Def and M.Def by +300% for 15 seconds.",
        type: "active",
        requiredLevel: 10,
        spCost: 500,
        range: 0,
        buffDuration: 15,
        selfCast: true,
        buff: {
            id: "ultimate_defense",
            name: "Ultimate Defense",
            icon: "🏰",
            duration: 15,
            mods: { pdefMul: 4, mdefMul: 4, rooted: 1 },
        },
        vfx: "vortex",
    },
    battle_heal: {
        id: "battle_heal",
        name: "Battle Heal",
        icon: "💚",
        level: 1,
        mpCost: 25,
        cooldown: 2.5,
        castTime: 1.0,
        description: "Quick divine incantation restoring 180 HP to target or self.",
        type: "active",
        requiredLevel: 2,
        spCost: 100,
        range: 15,
        healAmount: 180,
        selfCast: true,
        vfx: "bloom",
    },
    soulshot_toggle: {
        id: "soulshot_toggle",
        name: "Auto-Soulshot",
        icon: "✨",
        level: 1,
        mpCost: 0,
        cooldown: 0.5,
        castTime: 0,
        description: "Automatically loads Soulshots on every melee attack to double physical output.",
        type: "toggle",
        requiredLevel: 1,
        spCost: 0,
        range: 0,
        vfx: null,
    },
};

/**
 * Mobs.
 *
 * `visual` drives the procedural body: `height` in metres at the shoulders,
 * `bulk` widens the torso and limbs, `crouch` drops the spine towards a
 * four-legged stance, and `tint` is the hide colour.
 */
export const MOBS = {
    keltir: {
        id: "keltir",
        name: "Snowbound Keltir",
        level: 1,
        maxHp: 80,
        hp: 80,
        patk: 12,
        pdef: 8,
        expReward: 45,
        spReward: 12,
        adenaMin: 15,
        adenaMax: 35,
        type: "passive",
        scale: 0.9,
        moveSpeed: 3.0,
        visual: { bulk: 0.85, crouch: 0.55, tint: [0.34, 0.27, 0.21], fur: true },
        drops: [
            { itemId: "hp_potion", chance: 0.35, min: 1, max: 2 },
            { itemId: "short_sword", chance: 0.08, min: 1, max: 1 },
            { itemId: "soulshot_ng", chance: 0.8, min: 10, max: 30 },
        ],
    },
    rime_wolf: {
        id: "rime_wolf",
        name: "Rime Wolf",
        level: 4,
        maxHp: 180,
        hp: 180,
        patk: 24,
        pdef: 18,
        expReward: 110,
        spReward: 35,
        adenaMin: 40,
        adenaMax: 85,
        type: "passive",
        scale: 1.1,
        moveSpeed: 4.4,
        visual: { bulk: 0.9, crouch: 0.7, tint: [0.42, 0.44, 0.48], fur: true },
        drops: [
            { itemId: "hp_potion", chance: 0.5, min: 1, max: 3 },
            { itemId: "mp_potion", chance: 0.25, min: 1, max: 2 },
            { itemId: "wooden_armor", chance: 0.1, min: 1, max: 1 },
        ],
    },
    reaver: {
        id: "reaver",
        name: "Drift Reaver",
        level: 8,
        maxHp: 420,
        hp: 420,
        patk: 48,
        pdef: 36,
        expReward: 280,
        spReward: 85,
        adenaMin: 120,
        adenaMax: 240,
        type: "aggressive",
        scale: 1.3,
        moveSpeed: 3.8,
        visual: { bulk: 1.35, crouch: 0.12, tint: [0.29, 0.38, 0.24], fur: false },
        drops: [
            { itemId: "long_sword", chance: 0.08, min: 1, max: 1 },
            { itemId: "soulshot_ng", chance: 0.9, min: 25, max: 60 },
        ],
    },
    hollow_warden: {
        id: "hollow_warden",
        name: "Hollow Warden",
        level: 14,
        maxHp: 850,
        hp: 850,
        patk: 82,
        pdef: 64,
        expReward: 620,
        spReward: 190,
        adenaMin: 320,
        adenaMax: 550,
        type: "aggressive",
        scale: 1.2,
        moveSpeed: 4.2,
        visual: { bulk: 0.6, crouch: 0.05, tint: [0.82, 0.81, 0.76], fur: false },
        drops: [
            { itemId: "demon_staff", chance: 0.05, min: 1, max: 1 },
            { itemId: "plate_armor", chance: 0.06, min: 1, max: 1 },
        ],
    },
    sentinel: {
        id: "sentinel",
        name: "Rimeback Sentinel",
        level: 20,
        maxHp: 1800,
        hp: 1800,
        patk: 125,
        pdef: 140,
        expReward: 1400,
        spReward: 420,
        adenaMin: 800,
        adenaMax: 1500,
        type: "passive",
        scale: 1.8,
        moveSpeed: 2.4,
        visual: { bulk: 1.8, crouch: 0.1, tint: [0.38, 0.4, 0.43], fur: false },
        drops: [
            { itemId: "dual_katana", chance: 0.04, min: 1, max: 1 },
            { itemId: "bow_of_peril", chance: 0.02, min: 1, max: 1 },
        ],
    },
    skarn: {
        id: "skarn",
        name: "Raid Boss: Skarn, the Drift Colossus",
        level: 30,
        maxHp: 12000,
        hp: 12000,
        patk: 320,
        pdef: 280,
        expReward: 15000,
        spReward: 5000,
        adenaMin: 25000,
        adenaMax: 50000,
        type: "boss",
        scale: 3.5,
        moveSpeed: 3.2,
        visual: { bulk: 2.2, crouch: 0.45, tint: [0.19, 0.13, 0.2], fur: false },
        drops: [
            { itemId: "dual_katana", chance: 1.0, min: 1, max: 1 },
            { itemId: "bow_of_peril", chance: 1.0, min: 1, max: 1 },
            { itemId: "plate_armor", chance: 1.0, min: 1, max: 1 },
        ],
    },
};

/**
 * Spawn table. Six rings of danger radiating out from Sastrugi Hold at the
 * origin — each ring a proper walk further than the last.
 */
export const SPAWNS = [
    // ring 1 — snowbound keltir, the clearing edge (~25-55 m)
    { mob: "keltir", x: 15, z: -30 },
    { mob: "keltir", x: 24, z: -42 },
    { mob: "keltir", x: -18, z: -36 },
    { mob: "keltir", x: 6, z: -48 },
    { mob: "keltir", x: -30, z: -22 },
    { mob: "keltir", x: 32, z: -14 },

    // ring 2 — rime wolves, the lee slope (~90-150 m)
    { mob: "rime_wolf", x: 54, z: -75 },
    { mob: "rime_wolf", x: 66, z: -60 },
    { mob: "rime_wolf", x: 48, z: -92 },
    { mob: "rime_wolf", x: 78, z: -95 },
    { mob: "rime_wolf", x: 40, z: -115 },

    // ring 3 — drift reavers, holding the ridge break (~150-230 m)
    { mob: "reaver", x: -75, z: -105 },
    { mob: "reaver", x: -90, z: -84 },
    { mob: "reaver", x: -104, z: -98 },
    { mob: "reaver", x: -95, z: -130 },
    { mob: "reaver", x: -60, z: -155 },

    // ring 4 — hollow wardens, the buried Vanguard garrison (~260-320 m)
    { mob: "hollow_warden", x: 105, z: 75 },
    { mob: "hollow_warden", x: 122, z: 88 },
    { mob: "hollow_warden", x: 145, z: 60 },
    { mob: "hollow_warden", x: 128, z: 110 },

    // ring 5 — rimeback sentinels, flanking what is left of it (~330-380 m)
    { mob: "sentinel", x: -105, z: 90 },
    { mob: "sentinel", x: -140, z: 115 },

    // far south — Skarn, the Drift Colossus
    { mob: "skarn", x: 0, z: -360 },
];

export const NPCS = [
    {
        id: "npc_sella",
        name: "Warden Sella",
        title: "Gatekeeper",
        position: { x: -24, z: 36 },
        dialogHeader: "May the Drift favor your steps, traveler.",
        dialogText:
            "I can warp your footing to any waypoint the Vanguard still holds. Beyond them, you walk like everyone else.",
        options: [
            { label: "Warp: Reaver Break (Free)", action: "teleport", targetId: "reaver_break" },
            { label: "Warp: Hollow Muster (Free)", action: "teleport", targetId: "hollow_muster" },
            { label: "Warp: Colossus Approach — Danger! (Free)", action: "teleport", targetId: "colossus_approach" },
            { label: "Heal MP & HP (the Drift's Blessing)", action: "heal" },
        ],
    },
    {
        id: "npc_borgen",
        name: "Trader Borgen",
        title: "Weapons & Goods Merchant",
        position: { x: 36, z: -24 },
        dialogHeader: "Welcome to the Hold, traveler.",
        dialogText:
            "Steel and Soulshots, forged right here over the Hold's own furnaces. Fair prices — the wind's hard enough on you as it is.",
        options: [
            { label: "Open Merchant Shop", action: "shop" },
            { label: "Ask about Soulshots", action: "talk", targetId: "soulshots" },
        ],
    },
    {
        id: "npc_ythel",
        name: "Vanguard-Sage Ythel",
        title: "Mentor & Keeper of the Old Musters",
        position: { x: 0, z: -60 },
        dialogHeader: "Knowledge is the true blade.",
        dialogText:
            "I guide fresh wardens through class transfer, and I remember what walks beyond the ridge better than most would like.",
        options: [
            { label: "Class Transfer Assessment", action: "class_transfer" },
            { label: "Quest: Keltir Cull", action: "quest", targetId: "quest_keltir_cull" },
            { label: "Quest: The Sentinel's Toll", action: "quest", targetId: "quest_sentinel_toll" },
            { label: "Quest: The Colossus Wakes", action: "quest", targetId: "quest_colossus" },
        ],
    },
    {
        id: "npc_ranna",
        name: "Scout Ranna",
        title: "Vanguard Outrider",
        position: { x: 58, z: -120 },
        dialogHeader: "Keep low past this point.",
        dialogText:
            "I watch the lee slope so the Hold doesn't have to. The wolf packs have been bolder since the last storm.",
        options: [
            { label: "Quest: Wolves on the Lee Slope", action: "quest", targetId: "quest_lee_slope" },
        ],
    },
    {
        id: "npc_doram",
        name: "Warden-Captain Doram",
        title: "Ridge Watch Commander",
        position: { x: -70, z: -140 },
        dialogHeader: "The reavers took the break; we're taking it back.",
        dialogText:
            "My own company is buried past the ridge, hollowed out and still marching. I'd see them put down properly before I see anything else.",
        options: [
            { label: "Quest: Break the Reavers", action: "quest", targetId: "quest_reaver_break" },
            { label: "Quest: Silence the Hollow Wardens", action: "quest", targetId: "quest_hollow_wardens" },
        ],
    },
];

/** Vanguard waypoints — points inside the one field, not separate zones. */
export const TELEPORTS = {
    reaver_break: { x: -70, z: -95 },
    hollow_muster: { x: 100, z: 70 },
    colossus_approach: { x: 0, z: -320 },
};

/**
 * Quests. Unlike the original these are not active from boot — they are offered
 * by their giver, accepted, and turned in for the reward.
 */
export const QUESTS = {
    quest_keltir_cull: {
        questId: "quest_keltir_cull",
        title: "Keltir Cull",
        description:
            "Vanguard-Sage Ythel needs 5 Snowbound Keltir driven off from the clearing edge.",
        giver: "npc_ythel",
        targetMobId: "keltir",
        requiredKills: 5,
        rewardAdena: 500,
        rewardExp: 350,
        rewardItemId: "short_sword",
    },
    quest_lee_slope: {
        questId: "quest_lee_slope",
        title: "Wolves on the Lee Slope",
        description:
            "Scout Ranna wants 8 Rime Wolves thinned out before the packs grow bold enough to test the Hold itself.",
        giver: "npc_ranna",
        targetMobId: "rime_wolf",
        requiredKills: 8,
        rewardAdena: 1200,
        rewardExp: 900,
        rewardItemId: "wooden_armor",
    },
    quest_reaver_break: {
        questId: "quest_reaver_break",
        title: "Break the Reavers",
        description:
            "Warden-Captain Doram wants 6 Drift Reavers cleared from the ridge break the Vanguard used to hold.",
        giver: "npc_doram",
        targetMobId: "reaver",
        requiredKills: 6,
        rewardAdena: 3200,
        rewardExp: 2400,
        rewardItemId: "long_sword",
    },
    quest_hollow_wardens: {
        questId: "quest_hollow_wardens",
        title: "Silence the Hollow Wardens",
        description:
            "Doram's own company, hollowed out and still marching. He wants 5 Hollow Wardens put down for good.",
        giver: "npc_doram",
        targetMobId: "hollow_warden",
        requiredKills: 5,
        rewardAdena: 8000,
        rewardExp: 6500,
        rewardItemId: "demon_staff",
    },
    quest_sentinel_toll: {
        questId: "quest_sentinel_toll",
        title: "The Sentinel's Toll",
        description:
            "One Rimeback Sentinel still stands guard over the buried muster. Ythel wants it brought down.",
        giver: "npc_ythel",
        targetMobId: "sentinel",
        requiredKills: 1,
        rewardAdena: 16000,
        rewardExp: 12000,
        rewardItemId: "dual_katana",
    },
    quest_colossus: {
        questId: "quest_colossus",
        title: "The Colossus Wakes",
        description:
            "Slay Skarn, the Drift Colossus, grinding down out of the far southern peaks!",
        giver: "npc_ythel",
        targetMobId: "skarn",
        requiredKills: 1,
        rewardAdena: 50000,
        rewardExp: 25000,
        rewardItemId: "bow_of_peril",
    },
};

export const CLASS_PROFILES = {
    "Human Knight": {
        name: "Human Knight",
        icon: "🛡️",
        description:
            "Resilient frontline guardian with high HP, heavy armor mastery, shield defense, and crowd control.",
        stats: { hp: 320, mp: 80, str: 40, dex: 30, con: 43, int: 21, patk: 24, pdef: 32 },
        startingWeapon: "short_sword",
    },
    "Elven Spellsinger": {
        name: "Elven Spellsinger",
        icon: "✨",
        description:
            "Master of high-speed elemental magic, casting hydro blasts and ice daggers with rapid casting speed.",
        stats: { hp: 210, mp: 280, str: 28, dex: 35, con: 27, int: 42, wit: 45, matk: 38, mdef: 28 },
        startingWeapon: "demon_staff",
    },
    "Dark Elf Assassin": {
        name: "Dark Elf Assassin",
        icon: "🗡️",
        description:
            "Deadly agility and critical strikes. High physical attack speed and lethal power strikes.",
        stats: { hp: 250, mp: 120, str: 45, dex: 42, con: 30, int: 25, patk: 38, critRate: 28 },
        startingWeapon: "long_sword",
    },
    "Dwarf Artisan": {
        name: "Dwarf Artisan",
        icon: "🔨",
        description:
            "Sturdy craftsman specializing in gathering resources, high inventory capacity, and blunt force damage.",
        stats: { hp: 380, mp: 90, str: 39, dex: 28, con: 48, int: 20, patk: 28, pdef: 36 },
        startingWeapon: "short_sword",
    },
};

/** Default hotbar, F1..F12. */
export const DEFAULT_HOTBAR = [
    { type: "skill", targetId: "attack" },
    { type: "skill", targetId: "power_strike" },
    { type: "skill", targetId: "hydro_blast" },
    { type: "skill", targetId: "battle_heal" },
    { type: "skill", targetId: "soulshot_toggle" },
    { type: "item", targetId: "hp_potion" },
    { type: "item", targetId: "mp_potion" },
    { type: "action", targetId: "sit" },
    { type: "action", targetId: "surf" },
    { type: "skill", targetId: "ice_dagger" },
    { type: "skill", targetId: "shield_bash" },
    { type: "skill", targetId: "ultimate_defense" },
];

export const ACTION_ICONS = {
    sit: "🧘",
    surf: "🏂",
    pickup: "✋",
};
